import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Goal } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface GoalsState {
  goals: Goal[];
  isLoading: boolean;
  error: string | null;
}

const initialState: GoalsState = {
  goals: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchGoals = createAsyncThunk(
  'goals/fetchGoals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.goals.list);
      return (response.data.data || response.data) as Goal[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch goals'));
    }
  }
);

export const createGoal = createAsyncThunk(
  'goals/createGoal',
  async (goalData: Partial<Goal>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.goals.create, goalData);
      return (response.data.data || response.data) as Goal;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create goal'));
    }
  }
);

export const updateGoal = createAsyncThunk(
  'goals/updateGoal',
  async ({ id, data }: { id: string; data: Partial<Goal> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.goals.update(id), data);
      return (response.data.data || response.data) as Goal;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update goal'));
    }
  }
);

export const deleteGoal = createAsyncThunk(
  'goals/deleteGoal',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.goals.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete goal'));
    }
  }
);

export const contributeToGoal = createAsyncThunk(
  'goals/contributeToGoal',
  async ({ id, amount }: { id: string; amount: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.goals.contribute(id), { amount });
      return (response.data.data || response.data) as Goal;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to contribute to goal'));
    }
  }
);

export const completeGoal = createAsyncThunk(
  'goals/completeGoal',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.goals.complete(id));
      return (response.data.data || response.data) as Goal;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to complete goal'));
    }
  }
);

// Slice
const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateGoalProgress: (state, action: PayloadAction<{ id: string; currentAmount: number }>) => {
      const goal = state.goals.find(g => g.id === action.payload.id);
      if (goal) {
        goal.currentAmount = action.payload.currentAmount;
        goal.updatedAt = new Date().toISOString();
        
        // Auto-complete if target reached
        if (goal.currentAmount >= goal.targetAmount && goal.status === 'active') {
          goal.status = 'completed';
        }
      }
    },
    // [T68P1-c] Optimistic update actions for offline sync
    addOptimisticGoal: (state, action: PayloadAction<Goal>) => {
      state.goals.unshift(action.payload);
    },
    replaceOptimisticGoal: (state, action: PayloadAction<{ optimisticId: string; realItem: Goal }>) => {
      const idx = state.goals.findIndex(g => g.id === action.payload.optimisticId);
      if (idx !== -1) state.goals[idx] = action.payload.realItem;
    },
    removeOptimisticGoal: (state, action: PayloadAction<string>) => {
      state.goals = state.goals.filter(g => g.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch goals
    builder
      .addCase(fetchGoals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.goals = action.payload;
      })
      .addCase(fetchGoals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create goal
    builder
      .addCase(createGoal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGoal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.goals.push(action.payload);
      })
      .addCase(createGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update goal
    builder
      .addCase(updateGoal.fulfilled, (state, action) => {
        const index = state.goals.findIndex(g => g.id === action.payload.id);
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(updateGoal.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete goal
    builder
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.goals = state.goals.filter(g => g.id !== action.payload);
      })
      .addCase(deleteGoal.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Contribute to goal
    builder
      .addCase(contributeToGoal.fulfilled, (state, action) => {
        const index = state.goals.findIndex(g => g.id === action.payload.id);
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(contributeToGoal.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Complete goal
    builder
      .addCase(completeGoal.fulfilled, (state, action) => {
        const index = state.goals.findIndex(g => g.id === action.payload.id);
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(completeGoal.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError, updateGoalProgress,
  addOptimisticGoal, replaceOptimisticGoal, removeOptimisticGoal,
} = goalsSlice.actions;
export default goalsSlice.reducer;
